import type {
  AtlasDataset,
  AtlasFileKey,
  MetricsFile,
  SourceProfile
} from '../../types/atlas-intel.types'
import { ATLAS_FILE_NAMES } from '../../types/atlas-intel.types'
import {
  parseCostsJSON,
  parseTiersJSON,
  parseCostsByServiceJSON,
  parseStorageCSV,
  parseOpsCSV,
  parseIndexesCSV,
  parseServerCSV,
  parseDeltaJSON,
  parseMetricsJSON,
  parseDeepReportJSON
} from './parsers'
import { authHeaders } from '../../query-ai/lib/auth'

export function platformDataBase(): string {
  if (typeof window === 'undefined') return ''
  const h = window.location.hostname
  if (h === 'localhost' || h === '127.0.0.1') return ''
  return process.env.VUE_APP_AI_API_URL || 'https://dev.services.leadconnectorhq.com'
}

export function platformDataUrl(path: string): string {
  return `${platformDataBase()}${path}`
}

// ─── Fetch helpers ────────────────────────────────────────────────────────────

async function fetchText(url: string, noCache = false): Promise<string> {
  // Raw text stored directly (localFiles upload path — no encoding needed)
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
    return url
  }
  // data: URLs cannot be fetched (blocked by CSP) — decode inline instead
  if (url.startsWith('data:')) {
    const commaIdx = url.indexOf(',')
    if (commaIdx === -1) throw new Error('Invalid data URL')
    const meta = url.slice(5, commaIdx)
    const payload = url.slice(commaIdx + 1)
    if (meta.includes('base64')) {
      // Properly decode UTF-8 bytes from base64 rather than treating as latin-1
      const binary = atob(payload)
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0))
      return new TextDecoder('utf-8').decode(bytes)
    }
    return decodeURIComponent(payload)
  }
  const init: RequestInit = noCache ? { cache: 'no-store' } : {}
  const resp = await fetch(url, init)
  if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`)
  return resp.text()
}

function resolveUrl(profile: SourceProfile, key: AtlasFileKey): string | null {
  if (profile.type === 'localFiles') {
    return profile.loadedFiles?.[key] ?? null
  }
  if (profile.fileMap?.[key]) {
    return profile.fileMap[key]
  }
  if (profile.baseUrl) {
    const base = profile.baseUrl.replace(/\/$/, '')
    return `${base}/${ATLAS_FILE_NAMES[key]}`
  }
  return null
}

// ─── Single file loader ───────────────────────────────────────────────────────

async function loadFile(
  profile: SourceProfile,
  key: AtlasFileKey
): Promise<{ text: string | null; error?: string }> {
  const url = resolveUrl(profile, key)
  if (!url) return { text: null, error: 'no_url' }
  try {
    const isLocal = profile.type === 'localBaseUrl' || profile.type === 'localFiles'
    const text = await fetchText(url, isLocal)
    return { text }
  } catch (e) {
    return { text: null, error: e instanceof Error ? e.message : String(e) }
  }
}

// ─── NestJS API loader ────────────────────────────────────────────────────────

export async function loadFromNestApi(range?: string, snapshotId?: string): Promise<AtlasDataset> {
  const parts: string[] = []
  if (range && range !== 'all') parts.push(`range=${encodeURIComponent(range)}`)
  if (snapshotId) parts.push(`snapshotId=${encodeURIComponent(snapshotId)}`)
  // Keep the first dashboard paint light. The full metrics payload can be
  // millions of rows in ClickHouse and is loaded on demand by the Ops page.
  parts.push('includeMetrics=0')
  const qs = parts.length ? `?${parts.join('&')}` : ''
  const url = `${platformDataBase()}/platform-data/query-ai/atlas-intel/dataset${qs}`
  const resp = await fetch(url, {
    headers: { ...authHeaders(url), Accept: 'application/json' },
    signal: AbortSignal.timeout(60000)
  })
  if (!resp.ok) throw new Error(`API ${resp.status}: ${resp.statusText}`)
  const data = await resp.json()

  // source tells us where data came from: 'clickhouse' | 'files'
  const dataSource: 'clickhouse' | 'files' = data.source === 'clickhouse' ? 'clickhouse' : 'files'

  // Build fileStatus — when source is clickhouse mark all present data as 'ok'
  const fileStatus: Record<AtlasFileKey, 'ok' | 'missing' | 'error'> = {
    costs: 'missing',
    tiers: 'missing',
    costs_by_service: 'missing',
    storage: 'missing',
    ops: 'missing',
    indexes: 'missing',
    server: 'missing',
    delta: 'missing',
    metrics: 'missing',
    deep_report: 'missing'
  }

  if (dataSource === 'clickhouse') {
    // Mark as 'ok' for any data that actually has rows
    if ((data.costs ?? []).length) fileStatus.costs = 'ok'
    if ((data.tiers ?? []).length) fileStatus.tiers = 'ok'
    if ((data.costsByService ?? []).length) fileStatus.costs_by_service = 'ok'
    if ((data.storage ?? []).length) fileStatus.storage = 'ok'
    if ((data.ops ?? []).length) fileStatus.ops = 'ok'
    if ((data.indexes ?? []).length) fileStatus.indexes = 'ok'
    if ((data.server ?? []).length) fileStatus.server = 'ok'
    if (data.delta) fileStatus.delta = 'ok'
    if (data.metrics) fileStatus.metrics = 'ok'
    if (data.deepReport?.recommendations?.length) fileStatus.deep_report = 'ok'
  } else if (data.fileStatus) {
    const fs = data.fileStatus as Record<string, boolean>
    if (fs['costs.json']) fileStatus.costs = 'ok'
    if (fs['tiers.json']) fileStatus.tiers = 'ok'
    if (fs['costs_by_service.json']) fileStatus.costs_by_service = 'ok'
    if (fs['storage.csv']) fileStatus.storage = 'ok'
    if (fs['ops.csv']) fileStatus.ops = 'ok'
    if (fs['indexes.csv']) fileStatus.indexes = 'ok'
    if (fs['server.csv']) fileStatus.server = 'ok'
    if (fs['delta.json']) fileStatus.delta = 'ok'
    if (fs['metrics.json']) fileStatus.metrics = 'ok'
    if (fs['deep_report.json']) fileStatus.deep_report = 'ok'
  }

  return {
    costs: data.costs ?? [],
    tiers: data.tiers ?? [],
    costsByService: data.costsByService ?? [],
    orgCosts: data.orgCosts ?? [],
    currentMonthKey: data.currentMonthKey ?? undefined,
    storage: data.storage ?? [],
    ops: data.ops ?? [],
    indexes: data.indexes ?? [],
    server: data.server ?? [],
    delta: data.delta ?? null,
    metrics: data.metrics ?? null,
    deepReport: data.deepReport ?? null,
    fileStatus,
    loadedAt: data.loadedAt ?? new Date().toISOString(),
    lastCollectedAt: data.lastCollectedAt ?? null,
    source: dataSource,
    deferred: data.deferred ?? undefined
  }
}

export async function loadMetricsFromNestApi(snapshotId?: string): Promise<MetricsFile | null> {
  const parts: string[] = []
  if (snapshotId) parts.push(`snapshotId=${encodeURIComponent(snapshotId)}`)
  const qs = parts.length ? `?${parts.join('&')}` : ''
  const url = `${platformDataBase()}/platform-data/query-ai/atlas-intel/metrics${qs}`
  const resp = await fetch(url, {
    headers: { ...authHeaders(url), Accept: 'application/json' },
    signal: AbortSignal.timeout(120000)
  })
  if (!resp.ok) throw new Error(`API ${resp.status}: ${resp.statusText}`)
  const payload = await resp.json()
  return payload?.data ?? null
}

// ─── Main load function ───────────────────────────────────────────────────────

export async function loadAtlasDataset(
  profile: SourceProfile,
  range?: string,
  snapshotId?: string
): Promise<AtlasDataset> {
  if (profile.type === 'nestApi') {
    return loadFromNestApi(range, snapshotId)
  }
  const fileStatus: Record<AtlasFileKey, 'ok' | 'missing' | 'error'> = {
    costs: 'missing',
    tiers: 'missing',
    costs_by_service: 'missing',
    storage: 'missing',
    ops: 'missing',
    indexes: 'missing',
    server: 'missing',
    delta: 'missing',
    metrics: 'missing',
    deep_report: 'missing'
  }

  async function tryLoad(key: AtlasFileKey) {
    const result = await loadFile(profile, key)
    if (result.text !== null) {
      fileStatus[key] = 'ok'
    } else if (result.error && result.error !== 'no_url') {
      fileStatus[key] = 'error'
    }
    return result.text
  }

  const [
    costsText,
    tiersText,
    costsByServiceText,
    storageText,
    opsText,
    indexesText,
    serverText,
    deltaText,
    metricsText,
    deepText
  ] = await Promise.all([
    tryLoad('costs'),
    tryLoad('tiers'),
    tryLoad('costs_by_service'),
    tryLoad('storage'),
    tryLoad('ops'),
    tryLoad('indexes'),
    tryLoad('server'),
    tryLoad('delta'),
    tryLoad('metrics'),
    tryLoad('deep_report')
  ])

  return {
    costs: costsText ? parseCostsJSON(costsText) : [],
    tiers: tiersText ? parseTiersJSON(tiersText) : [],
    costsByService: costsByServiceText ? parseCostsByServiceJSON(costsByServiceText) : [],
    orgCosts: [],
    storage: storageText ? parseStorageCSV(storageText) : [],
    ops: opsText ? parseOpsCSV(opsText) : [],
    indexes: indexesText ? parseIndexesCSV(indexesText) : [],
    server: serverText ? parseServerCSV(serverText) : [],
    delta: deltaText ? parseDeltaJSON(deltaText) : null,
    metrics: metricsText ? parseMetricsJSON(metricsText) : null,
    deepReport: deepText ? parseDeepReportJSON(deepText) : null,
    fileStatus,
    loadedAt: new Date().toISOString()
  }
}

// ─── Local file upload handling ───────────────────────────────────────────────

export const FILE_KEY_BY_NAME: Record<string, AtlasFileKey> = {
  'costs.json': 'costs',
  'tiers.json': 'tiers',
  'costs_by_service.json': 'costs_by_service',
  'storage.csv': 'storage',
  'ops.csv': 'ops',
  'indexes.csv': 'indexes',
  'server.csv': 'server',
  'delta.json': 'delta',
  'metrics.json': 'metrics',
  'deep_report.json': 'deep_report'
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}
